import { makeStyles } from '@material-ui/styles'
import { pick } from 'ramda'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listTablePrefs } from 'app/constants'
import type InferActionParams from 'core/actions/InferActionParams'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import DocumentMeta from 'core/components/DocumentMeta'
import SimpleLink from 'core/components/SimpleLink'
import ListContainer from 'core/containers/ListContainer'
import type { GridCellProps } from 'core/elements/grid/hooks/useGridRows'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { listKaapiClusterAddons, updateKaapiClusterAddon } from '../kaapi/cluster-addons/actions'
import {
  type ClusterAddonStatus,
  ETCD_BACKUP_ADDON_TYPE,
  getAddonDisplayName,
} from '../kaapi/cluster-addons/etcd-backup'
import { kaapiClusterAddonsSelector } from '../kaapi/cluster-addons/selectors'
import { AddonStatusBadge, Phase } from './AddonStatusBadge'
import BackupDetailsModal from './BackupDetailsModal'
import ConfigureEtcdBackupModal, { type EtcdBackupAddon } from './ConfigureEtcdBackupModal'

type ActionParams = InferActionParams<typeof listKaapiClusterAddons>
type Params = ActionParams & {}

const defaultParams: Params = {
  namespace: '',
}

const usePrefParams = createUsePrefParamsHook('Cluster Addons', listTablePrefs)

// FR-011: the row a colocated cluster shows for Backup before it has the add-on
const BACKUP_PLACEHOLDER_UID = 'etcd-backup-not-enabled'
const isBackupPlaceholder = (item) => item?.metadata?.uid === BACKUP_PLACEHOLDER_UID
const createBackupPlaceholder = (clusterName: string) => ({
  metadata: { uid: BACKUP_PLACEHOLDER_UID },
  spec: { type: ETCD_BACKUP_ADDON_TYPE, version: '-', clusterID: clusterName },
  status: { status: Phase.NotEnabled },
})

const getColumns = (
  handleOpenUpdateModal,
  handleOpenConfigure,
  handleOpenDetails,
  canConfigure: boolean,
) => [
  {
    key: 'spec.type',
    label: 'Name',
    // FR-008: readable add-on names, such as "Backup"
    CellComponent: ({ value }: GridCellProps<unknown, string>) => <>{getAddonDisplayName(value)}</>,
  },
  {
    key: 'spec.version',
    label: 'Version',
  },
  {
    key: 'status',
    label: 'Status',
    CellComponent: ({
      item,
    }: GridCellProps<{ spec?: { type?: string }; status?: ClusterAddonStatus }>) => (
      <AddonStatusBadge addonName={getAddonDisplayName(item?.spec?.type)} status={item?.status} />
    ),
  },
  {
    key: 'status.upgradeInfo',
    label: '',
    CellComponent: (props) => {
      const classes = useStyles()
      const isBackup =
        props?.item?.spec?.type === ETCD_BACKUP_ADDON_TYPE && !isBackupPlaceholder(props?.item)
      return (
        <div className={classes.actions}>
          {props?.item?.status?.upgradeInfo?.available && (
            <SimpleLink
              onClick={() => handleOpenUpdateModal(props?.item)}
              data-testid="update-addon-link"
            >
              Update Available ({props?.item?.status?.upgradeInfo?.available})
            </SimpleLink>
          )}
          {/* FR-010: only Backup has details, opened in a modal; read-only users can see them */}
          {isBackup && (
            <SimpleLink
              onClick={() => handleOpenDetails(props?.item)}
              data-testid="backup-details-link"
            >
              Details
            </SimpleLink>
          )}
          {/* FR-012: Backup is configurable, except for read-only users */}
          {canConfigure && props?.item?.spec?.type === ETCD_BACKUP_ADDON_TYPE && (
            <SimpleLink
              onClick={() => handleOpenConfigure(props?.item)}
              data-testid="configure-backup-link"
            >
              Configure
            </SimpleLink>
          )}
        </div>
      )
    },
  },
]

interface ListClusterAddonsProps {
  // A colocated control plane, the only kind that supports the Backup add-on
  isColocatedCluster?: boolean
  // FR-013: lets the cluster page pause its refresh while the Backup drawer is open
  onConfigureOpenChange?: (open: boolean) => void
}

export default function ListClusterAddons({
  isColocatedCluster = false,
  onConfigureOpenChange,
}: ListClusterAddonsProps) {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const [openUpdateModal, setOpenUpdateModal] = useState(false)
  const [version, setVersion] = useState('')
  const [updateAvailable, setUpdateAvailable] = useState('')
  const [name, setName] = useState('')
  const [configureAddon, setConfigureAddon] = useState<EtcdBackupAddon | null>(null)
  const [configureOpen, setConfigureOpen] = useState(false)
  const [detailsAddon, setDetailsAddon] = useState<EtcdBackupAddon | null>(null)
  const canConfigure = !isReadonlyUser()

  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const clustersAddons = useSelector(kaapiClusterAddonsSelector) || []

  // Load immediately if data empty; defer if already cached
  const { loading: loadingKaapiClusterAddons, reload: reloadKaapiClusterAddons } = useListAction(
    listKaapiClusterAddons,
    { loadOnDemand: clustersAddons.length > 0 },
  )
  const { update: updateAddonFn, updating, error, reset } = useUpdateAction(updateKaapiClusterAddon)

  const handleOpenUpdateModal = useCallback((item) => {
    setVersion(item?.spec?.version)
    setUpdateAvailable(item?.status?.upgradeInfo?.available)
    setName(item?.spec?.type)
    setOpenUpdateModal(true)
  }, [])

  const filteredClusterAddons = useMemo(
    () =>
      clustersAddons?.filter(
        (clusterAddon) => clusterAddon?.spec?.clusterID === k8sPluginGlobalPerfs?.cluster,
      ),
    [clustersAddons, k8sPluginGlobalPerfs],
  )

  const clusterName = k8sPluginGlobalPerfs?.cluster as string | undefined

  // FR-011: a colocated cluster without the Backup add-on still lists it, as not enabled.
  // Only once the list has loaded: while it loads it is empty, and the row would offer to
  // enable a Backup add-on the cluster may already have.
  const rows = useMemo(() => {
    const hasBackup = filteredClusterAddons.some(
      (addon) => addon?.spec?.type === ETCD_BACKUP_ADDON_TYPE,
    )
    return isColocatedCluster && !hasBackup && clusterName && !loadingKaapiClusterAddons
      ? [...filteredClusterAddons, createBackupPlaceholder(clusterName)]
      : filteredClusterAddons
  }, [filteredClusterAddons, isColocatedCluster, clusterName, loadingKaapiClusterAddons])

  // FR-012/013: open and close the Backup drawer
  const handleOpenConfigure = useCallback(
    (item) => {
      setConfigureAddon(isBackupPlaceholder(item) ? null : item)
      setConfigureOpen(true)
      onConfigureOpenChange?.(true)
    },
    [onConfigureOpenChange],
  )
  const handleCloseConfigure = useCallback(() => {
    setConfigureOpen(false)
    setConfigureAddon(null)
    onConfigureOpenChange?.(false)
  }, [onConfigureOpenChange])

  const handleSubmit = useCallback(async () => {
    const body = {
      spec: {
        version: updateAvailable,
      },
    }
    const { success } = await updateAddonFn({
      namespace: filteredClusterAddons?.[0]?.metadata?.namespace,
      customBody: body,
      name: `${name}-addon-${k8sPluginGlobalPerfs?.cluster}`,
    })
    if (success) {
      reset()
      reloadKaapiClusterAddons(true, true)
      setOpenUpdateModal(false)
    }
  }, [
    updateAvailable,
    k8sPluginGlobalPerfs,
    updateAddonFn,
    filteredClusterAddons?.[0]?.metadata?.namespace,
    reset,
    reloadKaapiClusterAddons,
  ])

  return (
    <>
      <DocumentMeta title="Cluster Addons Overview" data-testid="cluster-addons-overview" />
      {openUpdateModal && (
        <ConfirmationDialog
          title="Update Version"
          text={`${name} version will be updated from ${version} to ${updateAvailable}`}
          onConfirm={handleSubmit}
          onCancel={() => setOpenUpdateModal(false)}
          error={error}
          loading={loadingKaapiClusterAddons || updating}
          open
          data-testid="update-metalLB-dialog"
        />
      )}
      {/* FR-010: the Backup details */}
      {detailsAddon && (
        <BackupDetailsModal addon={detailsAddon} onClose={() => setDetailsAddon(null)} />
      )}
      {configureOpen && (
        <ConfigureEtcdBackupModal
          clusterName={clusterName}
          addon={configureAddon || undefined}
          onClose={handleCloseConfigure}
          onSaved={() => reloadKaapiClusterAddons(true, true)}
        />
      )}
      <ListContainer<any, any>
        searchTargets={['spec.type']}
        uniqueIdentifier="uid"
        loading={loadingKaapiClusterAddons}
        loadingMessage="Loading..."
        onRefresh={reloadKaapiClusterAddons}
        data={rows}
        columns={getColumns(
          handleOpenUpdateModal,
          handleOpenConfigure,
          setDetailsAddon,
          canConfigure,
        )}
        getParamsUpdater={getParamsUpdater}
        label="Cluster Addons"
        disableRowSelection
        showItemsCountInLabel
        showBreadcrumbs={false}
        data-testid="cluster-addons-list-container"
        {...pick(listTablePrefs, params)}
      />
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  // One link per line, such as Details above Configure on the Backup row
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
}))
